import { useCallback, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { GraphItem } from 'graph/GraphItem';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  ArdoqGraphComponentForwardRefType,
  useGraphViewPerformanceTracking,
} from 'tabview/graphComponent/ArdoqGraphComponent';
import {
  canBypassLimit,
  isAboveItemLimit,
} from 'tabview/graphComponent/graphItemLimit';
import { canBypassViewDataLimit } from 'views/environmentals/viewDataLimits';
import { relationshipDiagramBypassDataLimit } from 'tabview/relationshipDiagrams/actions';
import { GraphItemsModel } from 'tabview/graphComponent/types';
import { isPresentationMode } from 'env';
import { getExportsForYFilesView, notifyExportFailure } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import { defaultLegendOnClick } from 'viewSettings/getRightMenuConfig';
import { ViewIds } from '@ardoq/api-types';
import {
  Cursor,
  EdgePathLabelModel,
  ExteriorNodeLabelModel,
  ExteriorNodeLabelModelPosition,
  GenericLayoutData,
  GraphComponent,
  GraphViewerInputMode,
  HierarchicalLayoutConfig,
  HierarchicalLayoutData,
  HierarchicalLayoutEdgeDescriptor,
  HierarchicalLayoutRoutingStyle,
  IGroupBoundsCalculator,
  Insets,
  LayoutDescriptor,
  LayoutKeys,
  PortAdjustmentPolicy,
  ViewportDescriptor,
  ViewportLimitingMode,
} from '@ardoq/yfiles';
import { ArdoqLabelStyle } from 'yfilesExtensions/styles/ArdoqLabelStyle';
import { ArdoqEdgeStyle } from 'yfilesExtensions/styles/ArdoqEdgeStyle';
import { TableGraphBuilder } from 'yfilesExtensions/TableGraphBuilder';
import { getViewModel$ } from './viewModel$';
import { SwimlaneViewModel, SwimlaneViewProperties } from './types';
import {
  onLayoutGraphFactory,
  type OnLayoutGraphArgs,
} from 'tabview/graphComponent/onLayoutGraph';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import { ViewAndLegendContainer } from 'tabview/ViewAndLegendContainer';
import { getSharedExportFunctions } from 'tabview/getSharedExportFunctions';
import { SwimlaneSettingsBar } from './SwimlaneSettingsBar';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { isEmptyView } from './utils';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { ArdoqGraphComponentWithLegend } from 'tabview/ArdoqGraphComponentWithLegend';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.SWIMLANE;

const SWIMLANE_HOVER_MODE = {
  edgeHover: false,
  highlightAdjacent: true,
  transparentizeNonAdjacent: false,
};

const Container = styled.div`
  .table-background {
    fill: transparent;
    fill-opacity: 0;
    stroke: #8dc5ee;
    stroke-width: 4px;
  }
  .table-line {
    fill: rgb(200, 200, 200);
    stroke: none;
  }
  .stripe-inset {
    fill: #dceffc;
    stroke: #8dc5ee;
    stroke-width: 2px;
  }
`;

const configureRendering = (graphComponent: GraphComponent) => {
  graphComponent.maximumZoom = 1.6;
  graphComponent.minimumZoom = 0.01;
  graphComponent.mouseWheelZoomFactor = 1.1;
  graphComponent.graph.edgeDefaults.style = new ArdoqEdgeStyle();
  // Style and labels
  const labelModel = new ExteriorNodeLabelModel();
  labelModel.margins = new Insets(15);
  graphComponent.graph.nodeDefaults.labels.layoutParameter =
    labelModel.createParameter(ExteriorNodeLabelModelPosition.BOTTOM);

  const labelStyle = ArdoqLabelStyle.Instance;
  graphComponent.graph.edgeDefaults.labels.style = labelStyle;
  graphComponent.graph.nodeDefaults.labels.style = labelStyle;

  const edgeLabelModel = new EdgePathLabelModel({
    autoRotation: false,
  });
  edgeLabelModel.distance = 5;
  graphComponent.graph.edgeDefaults.labels.layoutParameter =
    edgeLabelModel.createRatioParameter();
  (graphComponent.inputMode as GraphViewerInputMode).defaultCursor =
    Cursor.GRAB;
};

const viewModelToArray = <TItem extends GraphItem>(
  viewModel: GraphItemsModel<TItem>
) => viewModel.add.map(id => viewModel.byId.get(id)!);

export const SwimlaneView = (props: SwimlaneViewProperties) => {
  const ardoqGraphComponentRef =
    useRef<ArdoqGraphComponentForwardRefType>(null);
  const graphBuilder = useRef<TableGraphBuilder | null>(null);

  const getContainer = () => containerRef.current;

  const getGraphComponent = useCallback(
    () => ardoqGraphComponentRef.current?.graphComponent?.current ?? null,
    [ardoqGraphComponentRef]
  );

  const buildGraph = useCallback(
    (
      graphComponent: GraphComponent,
      viewModel: SwimlaneViewModel,
      bypassLimit: boolean,
      clearEdges: boolean,
      isVertical: boolean
    ) => {
      graphBuilder.current =
        graphBuilder.current ||
        new TableGraphBuilder({ graph: graphComponent.graph });

      graphBuilder.current.setVertical(isVertical);
      graphBuilder.current.scenarioRelatedComponentIds =
        viewModel.scenarioRelatedComponentIds;
      if (clearEdges) {
        graphBuilder.current.clearEdges();
      }
      const rows = viewModelToArray(viewModel.rows);
      const children = viewModelToArray(viewModel.nodes);
      const edges = viewModelToArray(viewModel.edges);

      const graphItemLimitArgs = {
        viewId: VIEW_ID,
        nodeCount: rows.length + children.length,
        edgeCount: edges.length,
        presentationMode: isPresentationMode(),
      };
      const canBypass = props.dataLimitExceeded
        ? canBypassViewDataLimit(VIEW_ID, props.dataLimitExceeded.itemCount)
        : canBypassLimit(graphItemLimitArgs);
      const isEmpty = !rows.length;
      const hasLayoutUpdate = graphBuilder.current.hasLayoutUpdate({
        rows,
        nodes: children,
        edges,
      });
      const aboveLimit = props.dataLimitExceeded
        ? true
        : isAboveItemLimit(graphItemLimitArgs);
      if ((!bypassLimit && aboveLimit) || isEmpty) {
        graphBuilder.current.nodesSource = [];
        graphBuilder.current.edgesSource = [];
        graphBuilder.current.rowsSource = [];
        graphBuilder.current.clear();
      } else {
        const isReordered = graphBuilder.current.rowsSource.some((row, i) => {
          return (
            rows.findIndex(otherRow => otherRow.dataModel === row.dataModel) !==
            i
          );
        });
        graphBuilder.current.nodesSource = children;
        graphBuilder.current.edgesSource = edges;
        graphBuilder.current.rowsSource = rows;
        graphBuilder.current.updateGraph({ isReordered });
      }

      return {
        isAboveLimit: aboveLimit,
        isEmpty,
        canBypass,
        hasLayoutUpdate,
      };
    },
    [graphBuilder, props.dataLimitExceeded]
  );

  const configureLayout = useCallback(
    ({ graph }: GraphComponent) => {
      const layoutConfig: HierarchicalLayoutConfig = {
        defaultEdgeDescriptor: new HierarchicalLayoutEdgeDescriptor({
          routingStyleDescriptor: {
            defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
          },
          backLoopRouting: false,
        }),
        nodeLabelPlacement: 'consider',
        edgeLabelPlacement: 'integrated',
        groupLayeringPolicy: 'recursive',
        layoutOrientation: props.viewSettings.isVertical
          ? 'top-to-bottom'
          : 'left-to-right',
        fromScratchLayeringStrategy: 'hierarchical-optimal',
      };

      const layoutDescriptor: LayoutDescriptor = {
        name: 'HierarchicalLayout',
        properties: layoutConfig,
      };
      const customMappingLayoutData = new GenericLayoutData();
      customMappingLayoutData.addItemMapping(
        LayoutKeys.MINIMUM_GROUP_NODE_SIZE_DATA_KEY,
        groupNode => {
          const boundsProvider = groupNode.lookup(IGroupBoundsCalculator);
          const bounds = boundsProvider!.calculateBounds(graph);
          return bounds.toSize();
        }
      );
      return {
        layoutDescriptor,
        layoutData: new HierarchicalLayoutData(),
        updateContentRect: true,
        portAdjustmentPolicy: PortAdjustmentPolicy.ALWAYS, // this causes the edges to respect NodeStyle.getOutline when deciding where to land on the node's outline.
      };
    },
    [props.viewSettings.isVertical]
  );

  useEffect(() => {
    if (!getGraphComponent()) {
      // clearing graphBuilder when no graphComponent available
      // required to redraw the graph when coming from an empty state
      graphBuilder.current = null;
    }
  });

  const {
    viewSettings,
    viewModel,
    zoomComponentId,
    viewInstanceId,
    clearEdges,
    preserveViewport,
    i18nLocale,
    legendProps,
  } = props;

  const onBypassDataLimit = useCallback(
    () =>
      dispatchAction(
        relationshipDiagramBypassDataLimit({ viewId: VIEW_ID }),
        viewInstanceId
      ),
    [viewInstanceId]
  );
  const { urlFieldValuesByComponentId, urlFieldValuesByReferenceId } =
    viewModel;
  const isLegendActive = viewSettings.isLegendActive ?? false;

  const isEmpty = !props.dataLimitExceeded && isEmptyView(viewModel);

  const openDegreesViewSetting = useRef(simpleNotifier());

  const buildGraphCallback = useCallback(
    (graphComponent: GraphComponent, bypassLimit: boolean) =>
      buildGraph(
        graphComponent,
        viewModel,
        bypassLimit,
        clearEdges,
        viewSettings.isVertical
      ),
    [buildGraph, viewModel, clearEdges, viewSettings.isVertical]
  );

  const sharedOnLayoutGraph = useMemo(
    () => onLayoutGraphFactory({ zoomComponentId }),
    [zoomComponentId]
  );
  const onLayoutGraph = useCallback(
    (args: OnLayoutGraphArgs) => {
      if (preserveViewport) {
        // Hold the user's camera in place across an in-place edit (component
        // attribute change, reference edit). yFiles doesn't auto-change
        // `viewport.center` / `graphComponent.zoom` during a layout — nodes
        // just shift around — so re-applying the current values (clamped
        // through the viewport limiter so we stay inside the new content
        // bounds) is enough. The previous implementation in
        // `zoomViewport(preservedViewport, …)` used `preservedWidth /
        // contentWidth` as the zoom level, which produced wildly varying
        // camera positions whenever content grew/shrank.
        const { graphComponent } = args;
        const { center, zoom } = graphComponent.viewportLimiter.limitViewport(
          graphComponent,
          new ViewportDescriptor(
            graphComponent.viewport.center,
            graphComponent.zoom
          ),
          ViewportLimitingMode.PROGRAMMATIC_NAVIGATION
        );
        graphComponent.zoomTo(zoom, center);
        return;
      }
      sharedOnLayoutGraph(args);
    },
    [preserveViewport, sharedOnLayoutGraph]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <Container className={`tab-pane swimlane-view active`}>
      <div className="menuContainer">
        <SwimlaneSettingsBar
          legendOnClick={() => {
            defaultLegendOnClick({
              isLegendActive: !isLegendActive,
              viewId: VIEW_ID,
            });
          }}
          exports={{
            ...getExportsForYFilesView({
              container: getContainer,
              graphComponent: getGraphComponent,
              exportedViewMetadata: {
                name: VIEW_ID,
                isViewpointMode: false,
              },
              showExportFailureModal: format =>
                notifyExportFailure(format, getExportLabels(i18nLocale)),
              ...getSharedExportFunctions(),
              labels: getExportLabels(i18nLocale),
            }),
            isDisabled: isEmpty,
          }}
          openDegreesViewSetting={openDegreesViewSetting.current}
          viewSettings={viewSettings}
          i18nLocale={i18nLocale}
        />
      </div>

      {isEmpty ? (
        <EmptyStateBase
          noConnectedComponents={viewModel.noConnectedComponents}
          isZeroDegrees={
            !viewSettings.incomingDegreesOfRelationship &&
            !viewSettings.outgoingDegreesOfRelationship
          }
          openDegreesViewSetting={openDegreesViewSetting.current}
        />
      ) : (
        <ViewAndLegendContainer ref={containerRef}>
          <ArdoqGraphComponentWithLegend
            ref={ardoqGraphComponentRef}
            viewId={VIEW_ID}
            viewModel={viewModel}
            buildGraph={buildGraphCallback}
            enableStyles={true}
            configureLayout={configureLayout}
            configureRendering={configureRendering}
            onLayoutGraph={onLayoutGraph}
            hoverMode={SWIMLANE_HOVER_MODE}
            viewInstanceId={viewInstanceId}
            legendProps={legendProps}
            isLegendActive={isLegendActive}
            isViewpointMode={false}
            urlFieldValuesByComponentId={urlFieldValuesByComponentId}
            urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
            i18nLocale={i18nLocale}
            hasUserBypassedDataLimit={props.hasUserBypassedDataLimit}
            onBypassDataLimit={onBypassDataLimit}
          />
        </ViewAndLegendContainer>
      )}
    </Container>
  );
};

const PerformanceTrackedSwimlaneView = (props: SwimlaneViewProperties) => {
  useGraphViewPerformanceTracking(props.viewModel, VIEW_ID);
  return <SwimlaneView {...props} />;
};

const SwimlaneViewWithLoadingIndicator = (props: SwimlaneViewProperties) => (
  <WithLoadingIndicator
    viewInstanceId={props.viewInstanceId}
    loadingSource="notifyViewLoading"
    showContentWhileLoading={false}
    i18nLocale={props.i18nLocale}
  >
    <PerformanceTrackedSwimlaneView {...props} />
  </WithLoadingIndicator>
);

const ConnectedSwimlaneView = connectInstanceWithProps(
  SwimlaneViewWithLoadingIndicator,
  getViewModel$
);

export { ConnectedSwimlaneView };
